import React, { useEffect, useRef, useState } from "react";
import "./ContextHeroSection.css";
import knotGraphicLanding1x from "@assets/images/atticus/icons/knotGraphicLanding-1220.webp";
import knotGraphicLanding2x from "@assets/images/atticus/icons/knotGraphicLanding-2440.webp";
import HeroBadge from "@shared/HeroBadge/HeroBadge";
// Context hero section
const featureItems = [
  {
    title: "Context Switching",
    body: (
      <>
        Legal employees lose <strong>9.5 minutes</strong> in billables every time
        they switch contexts.
      </>
    ),
    mobileLeft: "15.5%",
    mobileTop: "63%",
  },
  {
    title: "Context Missing",
    body: (
      <>
        Digital fatigue reduces employee performance by up to{" "}
        <strong>32%</strong>
      </>
    ),
    mobileLeft: "52%",
    mobileTop: "63%",
  },
  {
    title: "Context Stitching",
    body: (
      <>
        <strong>2.5 hours</strong> daily wasted searching &amp; stitching
        context
      </>
    ),
    mobileLeft: "78.5%",
    mobileTop: "63%",
  },
];

function ContextHeroSection() {
  const [showDesktopCta, setShowDesktopCta] = useState(false);
  const [showMobileCta, setShowMobileCta] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [desktopCtaPosition, setDesktopCtaPosition] = useState({
    left: "50%",
    top: "50%",
  });
  const [mobileCtaPosition, setMobileCtaPosition] = useState({
    left: "50%",
    top: "82%",
  });

  const desktopCtaRef = useRef(null);
  const desktopRafRef = useRef(null);
  const pendingDesktopPositionRef = useRef(null);
  const mobileScrollRef = useRef(null);
  const autoStopRef = useRef(-1);
  const manualHoldRef = useRef(0);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 680px)");
    const update = () => {
      const matches = mediaQuery.matches;
      setIsMobile(matches);
      if (!matches) setShowMobileCta(false);
    };
    update();

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", update);
      return () => mediaQuery.removeEventListener("change", update);
    }

    mediaQuery.addListener(update);
    return () => mediaQuery.removeListener(update);
  }, []);

  useEffect(() => {
    return () => {
      if (desktopRafRef.current) {
        cancelAnimationFrame(desktopRafRef.current);
        desktopRafRef.current = null;
      }
    };
  }, []);

  const scrollToVideo = () => {
    const videoSection = document.getElementById("watch-demo");
    if (!videoSection) return;
    setShowMobileCta(false);

    const wrapper = videoSection.querySelector(".watch-section__video-wrapper");
    const target = wrapper || videoSection;

    const rect = target.getBoundingClientRect();
    const NAV_OFFSET = 16;
    const targetTop = Math.max(0, window.scrollY + rect.top - NAV_OFFSET);

    window.scrollTo({ top: targetTop, behavior: "smooth" });
  };

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  // Mobile: the knot is far wider than the screen, so drive its horizontal
  // position from the page scroll -- left, then centre, then right -- rather
  // than asking the reader to discover a sideways swipe.
  useEffect(() => {
    const track = mobileScrollRef.current;
    if (!isMobile || !track) return undefined;

    const reduceMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let frame = null;

    const stopsFor = () => {
      const maxScroll = track.scrollWidth - track.clientWidth;
      if (maxScroll <= 0) return null;
      // Each feature is centred on its own `left`, so its offsetLeft IS the
      // point we want under the middle of the viewport.
      const features = Array.from(
        track.querySelectorAll(".context-hero__mobile-feature")
      );
      if (features.length < 2) return [0, maxScroll / 2, maxScroll];
      return features.map((node) =>
        clamp(node.offsetLeft - track.clientWidth / 2, 0, maxScroll)
      );
    };

    const sync = () => {
      frame = null;
      if (Date.now() < manualHoldRef.current) return;

      const stops = stopsFor();
      if (!stops) return;

      const rect = track.getBoundingClientRect();
      const viewport = window.innerHeight || document.documentElement.clientHeight;
      // 0 as the knot enters from the bottom, 1 once it has left the top.
      const progress = clamp(
        (viewport - rect.top) / (viewport + rect.height),
        0,
        1
      );
      const index = progress < 0.42 ? 0 : progress < 0.62 ? 1 : stops.length - 1;
      if (index === autoStopRef.current) return;

      autoStopRef.current = index;
      track.scrollTo({
        left: stops[index],
        behavior: reduceMotion ? "auto" : "smooth",
      });
    };

    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(sync);
    };

    // A reader who swipes the knot themselves owns it for a few seconds.
    const holdManual = () => {
      manualHoldRef.current = Date.now() + 2500;
      autoStopRef.current = -1;
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    track.addEventListener("touchstart", holdManual, { passive: true });
    track.addEventListener("touchend", holdManual, { passive: true });
    sync();

    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      track.removeEventListener("touchstart", holdManual);
      track.removeEventListener("touchend", holdManual);
    };
  }, [isMobile]);


  const getCtaDimensions = () => {
    const node = desktopCtaRef.current;
    if (!node) return { width: 170, height: 52 };
    return {
      width: node.offsetWidth || 170,
      height: node.offsetHeight || 52,
    };
  };

  const handleDesktopVisualMove = (event) => {
    if (isMobile) return;

    const visualBounds = event.currentTarget.getBoundingClientRect();
    const { width: ctaWidth, height: ctaHeight } = getCtaDimensions();
    const padding = 16;

    const rawLeft = event.clientX - visualBounds.left;
    const rawTop = event.clientY - visualBounds.top;

    const safeLeft = clamp(
      rawLeft,
      ctaWidth / 2 + padding,
      visualBounds.width - ctaWidth / 2 - padding
    );
    const safeTop = clamp(
      rawTop,
      ctaHeight / 2 + padding,
      visualBounds.height - ctaHeight / 2 - padding
    );

    const nextPosition = {
      left: `${(safeLeft / visualBounds.width) * 100}%`,
      top: `${(safeTop / visualBounds.height) * 100}%`,
    };

    pendingDesktopPositionRef.current = nextPosition;

    if (desktopRafRef.current) return;
    desktopRafRef.current = requestAnimationFrame(() => {
      if (pendingDesktopPositionRef.current) {
        setDesktopCtaPosition(pendingDesktopPositionRef.current);
      }
      desktopRafRef.current = null;
    });
  };

  const handleDesktopVisualEnter = (event) => {
    if (isMobile) return;
    setShowDesktopCta(true);
    handleDesktopVisualMove(event);
  };

  const handleMobileCanvasClick = (event) => {
    if (!isMobile) return;

    const canvasBounds = event.currentTarget.getBoundingClientRect();
    const ctaWidth = 170;
    const ctaHeight = 52;
    const padding = 16;

    const rawLeft = event.clientX - canvasBounds.left;
    const rawTop = event.clientY - canvasBounds.top;

    const safeLeft = clamp(
      rawLeft,
      ctaWidth / 2 + padding,
      canvasBounds.width - ctaWidth / 2 - padding
    );
    const safeTop = clamp(
      rawTop,
      ctaHeight / 2 + padding,
      canvasBounds.height - ctaHeight / 2 - padding
    );

    setMobileCtaPosition({
      left: `${(safeLeft / canvasBounds.width) * 100}%`,
      top: `${(safeTop / canvasBounds.height) * 100}%`,
    });
    setShowMobileCta(true);
  };

  const handleMobileCtaClick = (event) => {
    if (!isMobile) return;
    event.preventDefault();
    event.stopPropagation();
    scrollToVideo();
  };

  return (
    <section
      className={`context-hero ${showMobileCta ? "context-hero--mobile-armed" : ""}`}
      aria-labelledby="context-hero-title"
    >
      <div className="context-hero__inner">
        <header className="context-hero__header">
          <HeroBadge
            text="Not another tool. Your next hire."
            className="primary context-hero__badge"
          />
          <h2 id="context-hero-title">
            Legal tech fails when it creates more work.
          </h2>
          <p>70% of legal & operational work is lost in tool sprawl, wrecking productivity across matters, deals, and internal ops.</p>
        </header>

        <div
          className="context-hero__visual"
          onMouseEnter={handleDesktopVisualEnter}
          onMouseMove={handleDesktopVisualMove}
          onMouseLeave={() => !isMobile && setShowDesktopCta(false)}
        >
          <div className="context-hero__ribbon-wrap">
            <img
              src={knotGraphicLanding1x}
              srcSet={`${knotGraphicLanding1x} 1220w, ${knotGraphicLanding2x} 2440w`}
              sizes="(max-width: 767px) 285vw, min(1220px, 112vw)"
              width="3917"
              height="1098"
              alt=""
              className="context-hero__ribbon"
            />
          </div>

          <button
            type="button"
            ref={desktopCtaRef}
            className={`context-hero__video-cta ${showDesktopCta ? "is-visible" : ""}`}
            onClick={scrollToVideo}
            aria-label="Play video"
            style={desktopCtaPosition}
          >
            <span className="context-hero__video-cta-icon" aria-hidden="true" />
            <span>Play Video</span>
          </button>
        </div>

        <div
          ref={mobileScrollRef}
          className="context-hero__mobile-scroll"
          aria-label="Context management impacts"
        >
          <div
            className="context-hero__mobile-canvas"
            onClick={handleMobileCanvasClick}
          >
            <picture>
              <source
                media="(max-width: 680px)"
                srcSet={`${knotGraphicLanding1x} 1220w`}
                sizes="285vw"
              />
              <img
                src={knotGraphicLanding1x}
                srcSet={`${knotGraphicLanding1x} 1220w, ${knotGraphicLanding2x} 2440w`}
                sizes="285vw"
                width="3917"
                height="1098"
                alt=""
                className="context-hero__mobile-ribbon"
              />
            </picture>
            {featureItems.map((feature) => (
              <article
                key={`${feature.title}-mobile`}
                className="context-hero__mobile-feature"
                style={{
                  "--feature-left": feature.mobileLeft,
                  "--feature-top": feature.mobileTop,
                }}
              >
                <div className="context-hero__feature-heading context-hero__feature-heading--mobile">
                  <span className="context-hero__feature-connector" />
                  <h3>{feature.title}</h3>
                </div>
                <p>{feature.body}</p>
              </article>
            ))}

            <button
              type="button"
              className={`context-hero__video-cta context-hero__video-cta--mobile ${showMobileCta ? "is-visible" : ""}`}
              onClick={handleMobileCtaClick}
              aria-label="Play video"
              style={mobileCtaPosition}
            >
              <span
                className="context-hero__video-cta-icon"
                aria-hidden="true"
              />
              <span>Play Video</span>
            </button>
          </div>
        </div>

        <div className="context-hero__features">
          {featureItems.map((feature) => (
            <article key={feature.title} className="context-hero__feature">
              <div className="context-hero__feature-heading">
                <span className="context-hero__feature-connector" />
                <h3>{feature.title}</h3>
              </div>
              <p>{feature.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export default ContextHeroSection;
